import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { cn } from "cn";
import { useCallback, useEffect, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { SelectClearFrame, SelectTrigger } from "./select-trigger";

/**
 * A headless, data-agnostic single-select dropdown option. `value` is the
 * canonical entity ID (string) — selection is ALWAYS compared as
 * `option.value === value`, never by object identity (Trap T1). `detail` is
 * the custom row body inside the dropdown list (avatar + name, place-type
 * icon, …); the primitive appends the selection checkmark itself. `trigger` is
 * the custom inner content shown in the collapsed trigger when this option is
 * selected — a `renderTrigger` callback reads `selected.trigger` to render it
 * (falls back to `label` when omitted).
 */
export type ComboboxOption<T = unknown> = {
  value: string;
  label: string;
  trigger?: React.ReactNode;
  detail?: React.ReactNode;
  keywords?: string[];
  disabled?: boolean;
  data?: T;
  /** Optional heading; options sharing a `group` render under one `CommandGroup heading`. */
  group?: string;
};

export type ComboboxBaseProps = FieldWrapperInjectedProps & {
  isLoading?: boolean;
  /** Server-driven search box value. Omit for client-side cmdk filtering. */
  search?: string;
  onSearchChange?: (query: string) => void;
  /** Infinite/paged lists: more pages available + a loader to fetch them. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  align?: "start" | "center" | "end";
  className?: string;
  /**
   * cmdk client-side filtering. Defaults to OFF when `onSearchChange` is
   * provided (server-driven search), ON otherwise (fully-cached lists).
   */
  shouldFilter?: boolean;
  "data-test"?: string;
};

export type ComboboxProps<T = unknown> = ComboboxBaseProps & {
  value: string | null;
  onChange: (value: string | null) => void;
  options: ComboboxOption<T>[];
  /** Custom trigger inner content. Falls back to the selected option label. */
  renderTrigger?: (selected: ComboboxOption<T> | null) => React.ReactNode;
  contentClassName?: string;
  /** An X in the trigger sets the value to `null`. */
  clearable?: boolean;
  /** Accessible name of that X, naming what it clears ("Clear vendor"). Default "Clear". */
  clearLabel?: string;
};

export function Combobox<T = unknown>({
  value,
  onChange,
  options,
  renderTrigger,
  contentClassName,
  clearable = false,
  clearLabel,
  isLoading,
  search,
  onSearchChange,
  hasMore,
  isLoadingMore,
  onLoadMore,
  placeholder,
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  disabled,
  defaultOpen,
  open,
  onOpenChange,
  modal = true,
  align,
  shouldFilter,
  className,
  ...props
}: ComboboxProps<T>) {
  const [isOpen, setOpen] = useComboboxOpen({
    open,
    defaultOpen,
    onOpenChange,
    disabled,
  });

  const selected = options.find((option) => option.value === value) ?? null;
  const isServerSearch = typeof onSearchChange === "function";

  const renderOption = (option: ComboboxOption<T>) => (
    <CommandItem
      key={option.value}
      value={option.value}
      keywords={[option.label, ...(option.keywords ?? [])]}
      disabled={option.disabled}
      onSelect={() => {
        onChange(option.value);
        setOpen(false);
      }}
    >
      {option.detail ?? <span className="grow">{option.label}</span>}
      <BwCheckmarkLine
        size={16}
        className={cn(
          /* sorted */
          "ml-auto shrink-0",
          option.value === value ? "opacity-100" : "opacity-0",
        )}
      />
    </CommandItem>
  );

  // Group by `option.group` (preserving first-seen heading order) when any option has one.
  const groups = options.some((option) => option.group)
    ? Array.from(
        options.reduce((map, option) => {
          const heading = option.group ?? "";
          map.set(heading, [...(map.get(heading) ?? []), option]);
          return map;
        }, new Map<string, ComboboxOption<T>[]>()),
      )
    : null;

  return (
    <Popover modal={modal} open={isOpen} onOpenChange={setOpen}>
      <SelectClearFrame
        onClear={clearable ? () => onChange(null) : undefined}
        clearLabel={clearLabel}
        isEmpty={!value}
        disabled={disabled}
      >
        <PopoverTrigger disabled={disabled} className="group" asChild>
          <SelectTrigger
            {...props}
            disabled={disabled}
            isEmpty={!value}
            className={cn("text-sm", className)}
            hasClear={clearable}
          >
            {renderTrigger ? (
              renderTrigger(selected)
            ) : (
              <p className="text-sm line-clamp-1 grow">
                {selected ? selected.label : placeholder}
              </p>
            )}
          </SelectTrigger>
        </PopoverTrigger>
      </SelectClearFrame>
      <PopoverContent
        className={cn("p-0", contentClassName)}
        align={align}
        data-inline-edit-keep-open
      >
        <Command shouldFilter={shouldFilter ?? !isServerSearch}>
          <CommandInput
            placeholder={searchPlaceholder}
            {...(isServerSearch
              ? { value: search, onValueChange: onSearchChange }
              : {})}
          />
          <CommandList>
            {isLoading ? (
              <ComboboxListSkeleton />
            ) : (
              <>
                <CommandEmpty>{emptyText}</CommandEmpty>
                {groups ? (
                  groups.map(([heading, groupOptions]) => (
                    <CommandGroup key={heading} heading={heading || undefined}>
                      {groupOptions.map(renderOption)}
                    </CommandGroup>
                  ))
                ) : (
                  <CommandGroup>{options.map(renderOption)}</CommandGroup>
                )}
                {(isLoadingMore || hasMore) && (
                  <LoadMoreSentinel
                    hasMore={hasMore}
                    isLoadingMore={isLoadingMore}
                    onLoadMore={onLoadMore}
                  />
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Controllable open-state: controlled via `open`, else internal seeded by `defaultOpen`. */
export function useComboboxOpen({
  open,
  defaultOpen,
  onOpenChange,
  disabled,
}: {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (disabled) return;
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [disabled, isControlled, onOpenChange],
  );

  return [isOpen, setOpen] as const;
}

export function ComboboxListSkeleton() {
  return (
    <div className="flex flex-col gap-0.5 p-1">
      {Array.from({ length: 7 }).map((_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed 7-row skeleton placeholder, never reorders, no identity
        <Skeleton key={index} className="h-8" />
      ))}
    </div>
  );
}

/** Bottom-of-list intersection sentinel that calls `onLoadMore` when scrolled into view. */
export function LoadMoreSentinel({
  hasMore,
  isLoadingMore,
  onLoadMore,
}: {
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const [ref, entry] = useIntersectionObserver();

  useEffect(() => {
    if (entry?.isIntersecting && hasMore && !isLoadingMore) {
      onLoadMore?.();
    }
  }, [entry?.isIntersecting, hasMore, isLoadingMore, onLoadMore]);

  return (
    <div className="relative">
      {isLoadingMore && <ComboboxListSkeleton />}
      <div ref={ref} data-test="combobox-load-more" className="h-1" />
    </div>
  );
}
